import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import * as Dialog from '@radix-ui/react-dialog';
import { Layout } from '../components/Layout';
import { RelationshipsPanel } from '../components/RelationshipsPanel';
import { AuditLogPanel } from '../components/AuditLogPanel';
import { CollaboratorsPanel } from '../components/CollaboratorsPanel';
import { FamilyTreeView } from '../components/FamilyTreeView';
import { apiFetch, apiUpload } from '../lib/api';
import type { Person, TreeWithRole, PaginatedResponse, TreeGraph } from '@nasabtree/shared-types';

const PAGE_SIZE = 50;

const col = createColumnHelper<Person>();

function useColumns() {
  const { t } = useTranslation();
  return [
    col.accessor('ism_ar', {
      header: t('persons.name'),
      cell: (info) => (
        <span className="font-semibold text-gray-900" dir="rtl">
          {info.getValue() ?? '—'}
        </span>
      ),
    }),
    col.accessor('family_name', {
      header: t('persons.family_name'),
      cell: (info) => (
        <span className="text-gray-700" dir="rtl">
          {info.getValue() ?? '—'}
        </span>
      ),
    }),
    col.accessor('gender', {
      header: t('persons.gender'),
      cell: (info) => (
        <span
          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
            info.getValue() === 'male'
              ? 'bg-sky-50 text-sky-700'
              : 'bg-pink-50 text-pink-700'
          }`}
        >
          {info.getValue() === 'male' ? t('persons.gender.male') : t('persons.gender.female')}
        </span>
      ),
    }),
    col.accessor('birth_date_hijri', {
      header: t('persons.birth'),
      cell: (info) => <span className="text-gray-600" dir="ltr">{info.getValue() ?? '—'}</span>,
    }),
    col.accessor('death_date_hijri', {
      header: t('persons.death'),
      cell: (info) => <span className="text-gray-600" dir="ltr">{info.getValue() ?? '—'}</span>,
    }),
    col.accessor('is_living', {
      header: t('persons.living'),
      cell: (info) => (
        <span className={`inline-flex items-center gap-1 text-xs font-medium ${info.getValue() ? 'text-brand-600' : 'text-gray-400'}`}>
          {info.getValue() ? (
            <><span className="w-1.5 h-1.5 rounded-full bg-brand-500 inline-block" /> {t('persons.living')}</>
          ) : '—'}
        </span>
      ),
    }),
  ];
}

function AddPersonForm({
  treeId,
  defaultFamilyName,
  onSuccess,
  onCancel,
}: {
  treeId: string;
  defaultFamilyName?: string;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [ismAr, setIsmAr] = useState('');
  const [familyName, setFamilyName] = useState(defaultFamilyName ?? '');
  const [gender, setGender] = useState<'male' | 'female'>('male');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    const res = await apiFetch<Person>(`/api/trees/${treeId}/persons`, {
      method: 'POST',
      body: JSON.stringify({
        ism_ar: ismAr,
        family_name: familyName || null,
        gender,
      }),
    });
    if (res.ok) {
      onSuccess();
    } else {
      setError(res.error);
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      {error && <div className="text-red-600 text-sm rounded-lg bg-red-50 border border-red-200 px-3 py-2">{error}</div>}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {t('persons.ism_ar')} <span className="text-red-400">*</span>
        </label>
        <input
          required
          dir="rtl"
          value={ismAr}
          onChange={(e) => setIsmAr(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {t('persons.family_name')}
        </label>
        <input
          dir="rtl"
          value={familyName}
          onChange={(e) => setFamilyName(e.target.value)}
          className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {t('persons.gender')}
        </label>
        <div className="flex gap-3">
          <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${
            gender === 'male' ? 'border-sky-300 bg-sky-50 text-sky-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}>
            <input type="radio" name="gender" value="male" checked={gender === 'male'} onChange={() => setGender('male')} className="sr-only" />
            {t('persons.gender.male')}
          </label>
          <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${
            gender === 'female' ? 'border-pink-300 bg-pink-50 text-pink-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
          }`}>
            <input type="radio" name="gender" value="female" checked={gender === 'female'} onChange={() => setGender('female')} className="sr-only" />
            {t('persons.gender.female')}
          </label>
        </div>
      </div>
      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors">
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={saving}
          className="bg-brand-600 text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors shadow-sm"
        >
          {saving ? t('common.loading') : t('common.save')}
        </button>
      </div>
    </form>
  );
}

// ─── Reusable styles ──────────────────────────────────────────────────────────

const inputCls =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors';

const sectionHeadingCls = 'text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3';

// ─── Field display helper ─────────────────────────────────────────────────────

function Field({ label, value, dir }: { label: string; value: string | null | undefined; dir?: string }) {
  return (
    <div>
      <dt className="text-xs text-gray-400 mb-0.5">{label}</dt>
      <dd className="text-sm text-gray-900" dir={dir}>{value || '—'}</dd>
    </div>
  );
}

// ─── Person Detail Dialog ─────────────────────────────────────────────────────

type PersonForm = {
  ism_ar: string;
  ism_en: string;
  family_name: string;
  laqab_ar: string;
  laqab_en: string;
  nisba_ar: string;
  nisba_en: string;
  kunya_ar: string;
  kunya_en: string;
  gender: 'male' | 'female';
  birth_date_greg: string;
  birth_date_hijri: string;
  birth_date_approx: boolean;
  death_date_greg: string;
  death_date_hijri: string;
  death_date_approx: boolean;
  is_living: boolean;
  biography: string;
  profession_ar: string;
  profession_en: string;
  last_known_address: string;
  burial_location: string;
  burial_city: string;
  burial_country: string;
};

function personToForm(p: Person): PersonForm {
  return {
    ism_ar: p.ism_ar ?? '',
    ism_en: p.ism_en ?? '',
    family_name: p.family_name ?? '',
    laqab_ar: p.laqab_ar ?? '',
    laqab_en: p.laqab_en ?? '',
    nisba_ar: p.nisba_ar ?? '',
    nisba_en: p.nisba_en ?? '',
    kunya_ar: p.kunya_ar ?? '',
    kunya_en: p.kunya_en ?? '',
    gender: p.gender,
    birth_date_greg: p.birth_date_greg ?? '',
    birth_date_hijri: p.birth_date_hijri ?? '',
    birth_date_approx: p.birth_date_approx,
    death_date_greg: p.death_date_greg ?? '',
    death_date_hijri: p.death_date_hijri ?? '',
    death_date_approx: p.death_date_approx,
    is_living: p.is_living,
    biography: p.biography ?? '',
    profession_ar: p.profession_ar ?? '',
    profession_en: p.profession_en ?? '',
    last_known_address: p.last_known_address ?? '',
    burial_location: p.burial_location ?? '',
    burial_city: p.burial_city ?? '',
    burial_country: p.burial_country ?? '',
  };
}

function PersonDetailDialog({
  treeId,
  personId,
  canEdit,
  onClose,
  onUpdated,
  onDeleted,
}: {
  treeId: string;
  personId: string;
  canEdit: boolean;
  onClose: () => void;
  onUpdated: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const [person, setPerson] = useState<Person | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<PersonForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true);
    setEditing(false);
    setConfirmDelete(false);
    apiFetch<Person>(`/api/trees/${treeId}/persons/${personId}`).then((r) => {
      if (r.ok) {
        setPerson(r.data);
        setForm(personToForm(r.data));
      }
      setLoading(false);
    });
  }, [treeId, personId]);

  function updateField<K extends keyof PersonForm>(key: K, value: PersonForm[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  async function handleSave() {
    if (!form) return;
    setSaving(true);
    setError('');
    const body: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(form)) {
      body[k] = typeof v === 'string' && v === '' ? null : v;
    }
    const res = await apiFetch<Person>(`/api/trees/${treeId}/persons/${personId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setPerson(res.data);
      setForm(personToForm(res.data));
      setEditing(false);
      onUpdated();
    } else {
      setError(res.error);
    }
    setSaving(false);
  }

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('photo', file);
    const res = await apiUpload<Person>(
      `/api/trees/${treeId}/persons/${personId}/photo`,
      fd,
    );
    if (res.ok) {
      setPerson(res.data);
      onUpdated();
    }
    setUploading(false);
    // reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function handleDelete() {
    const res = await apiFetch<null>(`/api/trees/${treeId}/persons/${personId}`, {
      method: 'DELETE',
    });
    if (res.ok) {
      onDeleted();
      onClose();
    }
  }

  const photoUrl = person?.photo_r2_key
    ? `${import.meta.env.VITE_API_URL || ''}/api/trees/${treeId}/persons/${personId}/photo`
    : null;

  return (
    <Dialog.Root open onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl w-full max-w-2xl z-50 max-h-[90vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !person ? (
            <div className="p-6 text-center text-gray-500">{t('common.error')}</div>
          ) : editing && form ? (
            /* ── Edit Mode ─────────────────────────────────────────── */
            <div className="p-6 space-y-6">
              <Dialog.Title className="text-lg font-bold text-gray-900">
                {t('common.edit')} — {person.ism_ar ?? ''}
              </Dialog.Title>

              {error && (
                <div className="text-red-600 text-sm rounded-lg bg-red-50 border border-red-200 px-3 py-2">
                  {error}
                </div>
              )}

              {/* Photo upload */}
              <div className="flex items-center gap-4">
                <div className="relative w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                  {photoUrl ? (
                    <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <svg viewBox="0 0 24 24" className="w-8 h-8 text-gray-300" fill="currentColor">
                      <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                    </svg>
                  )}
                  {uploading && (
                    <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => void handlePhotoUpload(e)}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="text-sm text-brand-600 hover:text-brand-700 font-medium"
                  >
                    {photoUrl ? t('persons.changePhoto') : t('persons.uploadPhoto')}
                  </button>
                </div>
              </div>

              {/* Names section */}
              <div>
                <h3 className={sectionHeadingCls}>{t('persons.names')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.ism_ar')}</label>
                    <input dir="rtl" className={inputCls} value={form.ism_ar} onChange={(e) => updateField('ism_ar', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.ism_en')}</label>
                    <input dir="ltr" className={inputCls} value={form.ism_en} onChange={(e) => updateField('ism_en', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.family_name')}</label>
                    <input dir="rtl" className={inputCls} value={form.family_name} onChange={(e) => updateField('family_name', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.laqab_ar')}</label>
                    <input dir="rtl" className={inputCls} value={form.laqab_ar} onChange={(e) => updateField('laqab_ar', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.laqab_en')}</label>
                    <input dir="ltr" className={inputCls} value={form.laqab_en} onChange={(e) => updateField('laqab_en', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.nisba_ar')}</label>
                    <input dir="rtl" className={inputCls} value={form.nisba_ar} onChange={(e) => updateField('nisba_ar', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.nisba_en')}</label>
                    <input dir="ltr" className={inputCls} value={form.nisba_en} onChange={(e) => updateField('nisba_en', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.kunya_ar')}</label>
                    <input dir="rtl" className={inputCls} value={form.kunya_ar} onChange={(e) => updateField('kunya_ar', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.kunya_en')}</label>
                    <input dir="ltr" className={inputCls} value={form.kunya_en} onChange={(e) => updateField('kunya_en', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Gender */}
              <div>
                <label className="block text-xs text-gray-500 mb-1">{t('persons.gender')}</label>
                <div className="flex gap-3">
                  <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${
                    form.gender === 'male' ? 'border-sky-300 bg-sky-50 text-sky-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}>
                    <input type="radio" name="edit-gender" value="male" checked={form.gender === 'male'} onChange={() => updateField('gender', 'male')} className="sr-only" />
                    {t('persons.gender.male')}
                  </label>
                  <label className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${
                    form.gender === 'female' ? 'border-pink-300 bg-pink-50 text-pink-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}>
                    <input type="radio" name="edit-gender" value="female" checked={form.gender === 'female'} onChange={() => updateField('gender', 'female')} className="sr-only" />
                    {t('persons.gender.female')}
                  </label>
                </div>
              </div>

              {/* Dates section */}
              <div>
                <h3 className={sectionHeadingCls}>{t('persons.dates')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.birth_date_hijri')}</label>
                    <input dir="ltr" className={inputCls} placeholder="YYYY-MM-DD" value={form.birth_date_hijri} onChange={(e) => updateField('birth_date_hijri', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.birth_date_greg')}</label>
                    <input type="date" dir="ltr" className={inputCls} value={form.birth_date_greg} onChange={(e) => updateField('birth_date_greg', e.target.value)} />
                  </div>
                  <div className="col-span-2 flex items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input type="checkbox" checked={form.birth_date_approx} onChange={(e) => updateField('birth_date_approx', e.target.checked)} className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                      {t('persons.approximate')}
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input type="checkbox" checked={form.is_living} onChange={(e) => updateField('is_living', e.target.checked)} className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                      {t('persons.living')}
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.death_date_hijri')}</label>
                    <input dir="ltr" className={inputCls} placeholder="YYYY-MM-DD" value={form.death_date_hijri} onChange={(e) => updateField('death_date_hijri', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.death_date_greg')}</label>
                    <input type="date" dir="ltr" className={inputCls} value={form.death_date_greg} onChange={(e) => updateField('death_date_greg', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <label className="flex items-center gap-2 text-sm text-gray-600">
                      <input type="checkbox" checked={form.death_date_approx} onChange={(e) => updateField('death_date_approx', e.target.checked)} className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                      {t('persons.approximate')}
                    </label>
                  </div>
                </div>
              </div>

              {/* Details section */}
              <div>
                <h3 className={sectionHeadingCls}>{t('persons.details')}</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.biography')}</label>
                    <textarea dir="auto" rows={3} className={inputCls} value={form.biography} onChange={(e) => updateField('biography', e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">{t('persons.profession_ar')}</label>
                      <input dir="rtl" className={inputCls} value={form.profession_ar} onChange={(e) => updateField('profession_ar', e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">{t('persons.profession_en')}</label>
                      <input dir="ltr" className={inputCls} value={form.profession_en} onChange={(e) => updateField('profession_en', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.address')}</label>
                    <input dir="auto" className={inputCls} value={form.last_known_address} onChange={(e) => updateField('last_known_address', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Burial section */}
              <div>
                <h3 className={sectionHeadingCls}>{t('persons.burial')}</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.burial_location')}</label>
                    <input dir="auto" className={inputCls} value={form.burial_location} onChange={(e) => updateField('burial_location', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.burial_city')}</label>
                    <input dir="auto" className={inputCls} value={form.burial_city} onChange={(e) => updateField('burial_city', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">{t('persons.burial_country')}</label>
                    <input dir="auto" className={inputCls} value={form.burial_country} onChange={(e) => updateField('burial_country', e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  {t('common.delete')}
                </button>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => { setEditing(false); setForm(person ? personToForm(person) : null); setError(''); }}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void handleSave()}
                    className="bg-brand-600 text-white rounded-lg px-5 py-2 text-sm font-semibold hover:bg-brand-700 disabled:opacity-50 transition-colors shadow-sm"
                  >
                    {saving ? t('common.loading') : t('common.save')}
                  </button>
                </div>
              </div>

              {/* Delete confirmation */}
              {confirmDelete && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                  <p className="text-sm text-red-700 mb-3">{t('persons.deleteConfirm')}</p>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setConfirmDelete(false)} className="px-3 py-1.5 text-sm text-gray-600 rounded-lg hover:bg-gray-100">
                      {t('common.cancel')}
                    </button>
                    <button onClick={() => void handleDelete()} className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700">
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* ── View Mode ─────────────────────────────────────────── */
            <div className="p-6 space-y-6">
              {/* Header: photo + name + gender */}
              <div className="flex items-start gap-4">
                <div className="relative w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                  {photoUrl ? (
                    <img src={photoUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <svg viewBox="0 0 24 24" className="w-10 h-10 text-gray-300" fill="currentColor">
                      <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                    </svg>
                  )}
                  {canEdit && (
                    <>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => void handlePhotoUpload(e)}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center group"
                      >
                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                          <path d="M5.127 3.502 3.42 5.295a.752.752 0 0 1-.534.227H1.75A.75.75 0 0 0 1 6.272v10.478a.75.75 0 0 0 .75.75h16.5a.75.75 0 0 0 .75-.75V6.272a.75.75 0 0 0-.75-.75h-1.136a.752.752 0 0 1-.534-.227l-1.707-1.793a.752.752 0 0 0-.534-.227H5.661a.752.752 0 0 0-.534.227ZM10 15a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
                        </svg>
                        {uploading && (
                          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                            <div className="w-5 h-5 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
                          </div>
                        )}
                      </button>
                    </>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <Dialog.Title className="text-xl font-bold text-gray-900" dir="rtl">
                    {person.display_nasab_ar || person.ism_ar || '—'}
                  </Dialog.Title>
                  {person.display_nasab_en && (
                    <p className="text-sm text-gray-500 mt-0.5" dir="ltr">{person.display_nasab_en}</p>
                  )}
                  <span className={`inline-flex mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                    person.gender === 'male' ? 'bg-sky-50 text-sky-700' : 'bg-pink-50 text-pink-700'
                  }`}>
                    {person.gender === 'male' ? t('persons.gender.male') : t('persons.gender.female')}
                  </span>
                  {person.is_living && (
                    <span className="inline-flex items-center gap-1 ms-2 text-xs font-medium text-brand-600">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-500 inline-block" /> {t('persons.living')}
                    </span>
                  )}
                </div>
                {canEdit && (
                  <button
                    onClick={() => setEditing(true)}
                    className="shrink-0 px-3 py-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 border border-brand-200 rounded-lg hover:bg-brand-50 transition-colors"
                  >
                    {t('common.edit')}
                  </button>
                )}
              </div>

              {/* Names */}
              <div>
                <h3 className={sectionHeadingCls}>{t('persons.names')}</h3>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <Field label={t('persons.ism_ar')} value={person.ism_ar} dir="rtl" />
                  <Field label={t('persons.ism_en')} value={person.ism_en} dir="ltr" />
                  <Field label={t('persons.family_name')} value={person.family_name} dir="rtl" />
                  <Field label={t('persons.laqab_ar')} value={person.laqab_ar} dir="rtl" />
                  <Field label={t('persons.laqab_en')} value={person.laqab_en} dir="ltr" />
                  <Field label={t('persons.nisba_ar')} value={person.nisba_ar} dir="rtl" />
                  <Field label={t('persons.nisba_en')} value={person.nisba_en} dir="ltr" />
                  <Field label={t('persons.kunya_ar')} value={person.kunya_ar} dir="rtl" />
                  <Field label={t('persons.kunya_en')} value={person.kunya_en} dir="ltr" />
                </dl>
              </div>

              {/* Dates */}
              <div>
                <h3 className={sectionHeadingCls}>{t('persons.dates')}</h3>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <Field label={t('persons.birth_date_hijri')} value={person.birth_date_hijri} dir="ltr" />
                  <Field label={t('persons.birth_date_greg')} value={person.birth_date_greg} dir="ltr" />
                  {person.birth_date_approx && <dd className="text-xs text-amber-600 col-span-2">~ {t('persons.approximate')}</dd>}
                  <Field label={t('persons.death_date_hijri')} value={person.death_date_hijri} dir="ltr" />
                  <Field label={t('persons.death_date_greg')} value={person.death_date_greg} dir="ltr" />
                  {person.death_date_approx && <dd className="text-xs text-amber-600 col-span-2">~ {t('persons.approximate')}</dd>}
                </dl>
              </div>

              {/* Details */}
              {(person.biography || person.profession_ar || person.profession_en || person.last_known_address) && (
                <div>
                  <h3 className={sectionHeadingCls}>{t('persons.details')}</h3>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                    {person.biography && (
                      <div className="col-span-2">
                        <dt className="text-xs text-gray-400 mb-0.5">{t('persons.biography')}</dt>
                        <dd className="text-sm text-gray-900 whitespace-pre-wrap" dir="auto">{person.biography}</dd>
                      </div>
                    )}
                    <Field label={t('persons.profession_ar')} value={person.profession_ar} dir="rtl" />
                    <Field label={t('persons.profession_en')} value={person.profession_en} dir="ltr" />
                    {person.last_known_address && (
                      <Field label={t('persons.address')} value={person.last_known_address} dir="auto" />
                    )}
                  </dl>
                </div>
              )}

              {/* Burial */}
              {(person.burial_location || person.burial_city || person.burial_country) && (
                <div>
                  <h3 className={sectionHeadingCls}>{t('persons.burial')}</h3>
                  <dl className="grid grid-cols-3 gap-x-6 gap-y-3">
                    <Field label={t('persons.burial_location')} value={person.burial_location} dir="auto" />
                    <Field label={t('persons.burial_city')} value={person.burial_city} dir="auto" />
                    <Field label={t('persons.burial_country')} value={person.burial_country} dir="auto" />
                  </dl>
                </div>
              )}
            </div>
          )}

          {/* Close button */}
          <Dialog.Close asChild>
            <button
              className="absolute top-4 end-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function TreePage() {
  const { treeId } = useParams<{ treeId: string }>();
  const { t } = useTranslation();

  const [tree, setTree] = useState<TreeWithRole | null>(null);
  const [persons, setPersons] = useState<Person[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [detailPersonId, setDetailPersonId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'tree'>('table');
  const [graphData, setGraphData] = useState<TreeGraph | null>(null);
  const [graphLoading, setGraphLoading] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [collabOpen, setCollabOpen] = useState(false);

  const columns = useColumns();

  const table = useReactTable({
    data: persons,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: total,
  });

  useEffect(() => {
    if (!treeId) return;
    apiFetch<TreeWithRole>(`/api/trees/${treeId}`).then((r) => {
      if (r.ok) setTree(r.data);
    });
  }, [treeId]);

  const loadPersons = useCallback(async () => {
    if (!treeId) return;
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
    if (search) params.set('q', search);
    const res = await apiFetch<PaginatedResponse<Person>>(
      `/api/trees/${treeId}/persons?${params.toString()}`,
    );
    if (res.ok) {
      setPersons(res.data.items);
      setTotal(res.data.total);
    }
    setLoading(false);
  }, [treeId, page, search]);

  useEffect(() => { void loadPersons(); }, [loadPersons]);

  // Load graph data when switching to tree view
  useEffect(() => {
    if (viewMode !== 'tree' || !treeId) return;
    setGraphLoading(true);
    apiFetch<TreeGraph>(`/api/trees/${treeId}/graph`).then((res) => {
      if (res.ok) setGraphData(res.data);
      setGraphLoading(false);
    });
  }, [viewMode, treeId]);

  async function handleExport() {
    if (!treeId) return;
    const res = await apiFetch<Record<string, unknown>>(`/api/trees/${treeId}/export`);
    if (res.ok) {
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tree?.name_ar ?? 'tree'}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }

  function handleSearch(value: string) {
    setSearch(value);
    setPage(1);
  }

  const canEdit = tree?.role === 'owner' || tree?.role === 'editor';
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Layout>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" dir="rtl">
            {tree?.name_ar ?? '…'}
          </h1>
          {tree?.name_en && (
            <p className="text-sm text-gray-500 mt-0.5" dir="ltr">{tree.name_en}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-2 text-xs font-medium transition-colors ${
                viewMode === 'table' ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t('tree.viewTable')}
            </button>
            <button
              onClick={() => setViewMode('tree')}
              className={`px-3 py-2 text-xs font-medium transition-colors ${
                viewMode === 'tree' ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              {t('tree.viewTree')}
            </button>
          </div>

          {/* Export */}
          <button
            onClick={() => void handleExport()}
            className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            title={t('trees.export')}
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z" />
              <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z" />
            </svg>
          </button>

          {/* Owner-only buttons */}
          {tree?.role === 'owner' && (
            <>
              {/* Activity / Audit */}
              <button
                onClick={() => setAuditOpen(true)}
                className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                title={t('audit.title')}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm.75-13a.75.75 0 0 0-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 0 0 0-1.5h-3.25V5Z" clipRule="evenodd" />
                </svg>
              </button>

              {/* Collaborators */}
              <button
                onClick={() => setCollabOpen(true)}
                className="p-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                title={t('collaborators.title')}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M7 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM14.5 9a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM1.615 16.428a1.224 1.224 0 0 1-.569-1.175 6.002 6.002 0 0 1 11.908 0c.058.467-.172.92-.57 1.174A9.953 9.953 0 0 1 7 18a9.953 9.953 0 0 1-5.385-1.572ZM14.5 16h-.106c.07-.297.088-.611.048-.933a7.47 7.47 0 0 0-1.588-3.755 4.502 4.502 0 0 1 5.874 2.636.818.818 0 0 1-.36.98A7.465 7.465 0 0 1 14.5 16Z" />
                </svg>
              </button>
            </>
          )}

          {canEdit && (
            <Dialog.Root open={addOpen} onOpenChange={setAddOpen}>
              <Dialog.Trigger asChild>
                <button className="bg-brand-600 text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-brand-700 transition-colors shadow-sm flex items-center gap-1.5">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                  </svg>
                  {t('persons.add')}
                </button>
              </Dialog.Trigger>
              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 bg-black/40 z-40" />
                <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg z-50 max-h-[85vh] overflow-y-auto">
                  <Dialog.Title className="text-lg font-bold text-gray-900 mb-4">
                    {t('persons.add')}
                  </Dialog.Title>
                  <AddPersonForm
                    treeId={treeId!}
                    defaultFamilyName={tree?.name_ar}
                    onSuccess={() => { setAddOpen(false); void loadPersons(); }}
                    onCancel={() => setAddOpen(false)}
                  />
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          )}
        </div>
      </div>

      {viewMode === 'tree' ? (
        /* ── Tree Visualization Mode ─────────────────────────────── */
        graphLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : graphData ? (
          <FamilyTreeView
            graph={graphData}
            treeId={treeId!}
            selectedPersonId={selectedPersonId}
            onSelectPerson={(id) => setSelectedPersonId(id)}
            onViewDetails={(id) => setDetailPersonId(id)}
          />
        ) : null
      ) : (
      <>
      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400 absolute start-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" />
          </svg>
          <input
            type="search"
            dir="auto"
            placeholder={t('persons.search')}
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full sm:w-80 border border-gray-300 rounded-lg ps-9 pe-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
          />
        </div>
      </div>

      {/* Table + Relationships Panel */}
      <div className={`flex flex-col ${selectedPersonId ? 'lg:flex-row' : ''} gap-4`}>
        <div className={`${selectedPersonId ? 'lg:w-3/5' : 'w-full'} min-w-0`}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-6 h-6 border-2 border-brand-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : persons.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg viewBox="0 0 24 24" className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <p className="text-gray-900 font-semibold mb-1">{t('persons.emptyTitle', 'لا يوجد أفراد بعد')}</p>
              <p className="text-sm text-gray-500">{t('persons.empty')}</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    {table.getHeaderGroups().map((hg) => (
                      <tr key={hg.id}>
                        {hg.headers.map((h) => (
                          <th
                            key={h.id}
                            className="px-4 py-3 text-start text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                          >
                            {flexRender(h.column.columnDef.header, h.getContext())}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className={`transition-colors cursor-pointer ${
                          selectedPersonId === row.original.id
                            ? 'bg-brand-50 ring-1 ring-inset ring-brand-200'
                            : 'hover:bg-brand-50/40'
                        }`}
                        onClick={() => setSelectedPersonId(row.original.id)}
                        onDoubleClick={() => setDetailPersonId(row.original.id)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="px-4 py-3 text-gray-700 whitespace-nowrap">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <span className="font-medium">
                  {total} {t('persons.title')}
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors font-medium"
                    aria-label="Previous page"
                  >
                    ‹
                  </button>
                  <span className="px-3 py-1.5 text-sm font-medium">
                    {page} / {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50 transition-colors font-medium"
                    aria-label="Next page"
                  >
                    ›
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Relationships Panel — outside loading conditional so dialogs survive refreshes */}
        {selectedPersonId && treeId && (
          <div className="lg:w-2/5 min-w-0">
            <RelationshipsPanel
              treeId={treeId}
              personId={selectedPersonId}
              person={persons.find((p) => p.id === selectedPersonId) ?? null}
              canEdit={canEdit}
              onPersonsChanged={() => void loadPersons()}
              onClose={() => setSelectedPersonId(null)}
              onViewDetails={() => setDetailPersonId(selectedPersonId)}
              onSelectPerson={(id) => setSelectedPersonId(id)}
              defaultFamilyName={tree?.name_ar}
            />
          </div>
        )}
      </div>
      </>
      )}
      {/* Person Detail Dialog */}
      {detailPersonId && treeId && (
        <PersonDetailDialog
          treeId={treeId}
          personId={detailPersonId}
          canEdit={canEdit}
          onClose={() => setDetailPersonId(null)}
          onUpdated={() => void loadPersons()}
          onDeleted={() => { void loadPersons(); setSelectedPersonId(null); }}
        />
      )}
      {/* Audit Log Panel */}
      {treeId && (
        <AuditLogPanel treeId={treeId} open={auditOpen} onClose={() => setAuditOpen(false)} />
      )}
      {/* Collaborators Panel */}
      {treeId && (
        <CollaboratorsPanel treeId={treeId} open={collabOpen} onClose={() => setCollabOpen(false)} />
      )}
    </Layout>
  );
}
